import { ChevronLeft, ChevronRight, FolderPlus, Upload } from "lucide-react";
import { Fragment } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Spinner } from "@lawline/ui/components/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";
import { cn } from "@lawline/ui/core/cn";

export function FileBrowserToolbar({
	path,
	onNavigate,
	onBack,
	onForward,
	canBack,
	canForward,
	onNewFolder,
	onUpload,
	loading,
}: {
	path: string[];
	onNavigate: (index: number) => void;
	onBack: () => void;
	onForward: () => void;
	canBack: boolean;
	canForward: boolean;
	onNewFolder: () => void;
	onUpload: () => void;
	loading: boolean;
}) {
	const t = useTranslations("common.fileBrowser");
	return (
		<div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
			<div className="flex min-w-0 items-center gap-1">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex">
							<Button
								variant="ghost"
								size="icon"
								className="size-7"
								disabled={!canBack}
								onClick={onBack}
							>
								<ChevronLeft className="size-4" />
							</Button>
						</span>
					</TooltipTrigger>
					<TooltipContent>{t("back")}</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="inline-flex">
							<Button
								variant="ghost"
								size="icon"
								className="size-7"
								disabled={!canForward}
								onClick={onForward}
							>
								<ChevronRight className="size-4" />
							</Button>
						</span>
					</TooltipTrigger>
					<TooltipContent>{t("forward")}</TooltipContent>
				</Tooltip>
				<nav className="text-muted-foreground flex min-w-0 items-center font-mono text-sm">
					<button
					type="button"
					onClick={() => onNavigate(-1)}
					className="hover:bg-accent hover:text-foreground rounded px-1 py-0.5"
				>
					/
				</button>
				{path.map((segment, index) => (
					<Fragment key={index}>
						{index > 0 && <span>/</span>}
						<button
							type="button"
							onClick={() => onNavigate(index)}
							className="hover:bg-accent hover:text-foreground truncate rounded px-1 py-0.5"
						>
							{segment}
						</button>
					</Fragment>
				))}
				</nav>
			</div>
			<div className="flex shrink-0 items-center gap-1">
				<Spinner
					className={cn("text-muted-foreground size-4", !loading && "invisible")}
				/>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button variant="ghost" size="sm" onClick={onNewFolder}>
							<FolderPlus className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{t("newFolder")}</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button variant="ghost" size="sm" onClick={onUpload}>
							<Upload className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{t("upload")}</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);
}
